import React, { useEffect, useRef, useState } from 'react';
import { Navigation, Search, Download, Save, RefreshCw, Edit, Trash2, AlertTriangle, X, CheckCircle, List, Upload, FileEdit, MapPin } from 'lucide-react';
import MainService from '../../services/MainService';

const Locations = () => {
  const [locations, setLocations] = useState([]);
  const [areas, setAreas] = useState([]);
  const [emirates, setEmirates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [warning, setWarning] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  const [filteredRows, setFilteredRows] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [bulkEditDialogOpen, setBulkEditDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [bulkEditData, setBulkEditData] = useState([]);
  const [bulkEditLoading, setBulkEditLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    nameAr: '',
    emirateId: '',
    areaId: '',
    locationUrl: '',
    lat: '',
    lng: ''
  });

  // Leaflet refs/state
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const mapContainerRef = useRef(null);
  const leftCardRef = useRef(null);
  const mapCardRef = useRef(null);
  const mapHeaderRef = useRef(null);
  const [leafletReady, setLeafletReady] = useState(false);

  useEffect(() => {
    initLoad();
  }, []);

  // Toast notification effect
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (warning) {
      const timer = setTimeout(() => {
        setWarning('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [warning]);

  const initLoad = async () => {
    try {
      setLoading(true);
      const [emirs, ars] = await Promise.all([
        MainService.EmiratesInDropDown(),
        MainService.AreasInDropDown(),
      ]);
      setEmirates(emirs || []);
      setAreas(ars || []);
      await loadRows();
    } catch (err) {
      console.error('Init error:', err);
      setError('Failed to initialize');
    } finally {
      setLoading(false);
    }
  };

  // -------- Leaflet loader & map init (CDN, no other files touched) --------
  useEffect(() => {
    const ensureLeaflet = async () => {
      if (window.L) {
        injectLeafletCss();
        setLeafletReady(true);
        return;
      }
      injectLeafletCss();
      await new Promise((resolve, reject) => {
        const existing = document.querySelector('script[data-leaflet]');
        if (existing) {
          existing.addEventListener('load', () => resolve());
          existing.addEventListener('error', reject);
          return;
        }
        const s = document.createElement('script');
        s.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
        s.async = true;
        s.defer = true;
        s.setAttribute('data-leaflet', 'true');
        s.onload = () => resolve();
        s.onerror = reject;
        document.body.appendChild(s);
      });
      setLeafletReady(true);
    };

    const injectLeafletCss = () => {
      if (!document.querySelector('link[data-leaflet-css]')) {
        const l = document.createElement('link');
        l.rel = 'stylesheet';
        l.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
        l.setAttribute('data-leaflet-css', 'true');
        document.head.appendChild(l);
      }
    };

    ensureLeaflet();
  }, []);

  useEffect(() => {
    if (!leafletReady) return;
    if (!mapContainerRef.current) return;
    if (mapRef.current) return; // already initialized

    const L = window.L;
    const startLat = formData.lat ? parseFloat(formData.lat) : 23.4241; // UAE center
    const startLng = formData.lng ? parseFloat(formData.lng) : 53.8478;

    const map = L.map(mapContainerRef.current).setView([startLat, startLng], 8);
    mapRef.current = map;

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; OpenStreetMap'
    }).addTo(map);

    // marker
    markerRef.current = L.marker([startLat, startLng], { draggable: true }).addTo(map);

    markerRef.current.on('dragend', () => {
      const pos = markerRef.current.getLatLng();
      setFormData((prev) => ({ ...prev, lat: pos.lat.toFixed(6), lng: pos.lng.toFixed(6) }));
    });

    map.on('click', (e) => {
      const { lat, lng } = e.latlng;
      markerRef.current.setLatLng([lat, lng]);
      setFormData((prev) => ({ ...prev, lat: lat.toFixed(6), lng: lng.toFixed(6) }));
    });

    // --- Lightweight search control (Nominatim) inside the map ---
    const SearchControl = L.Control.extend({
      onAdd: function() {
        const container = L.DomUtil.create('div', 'leaflet-bar');
        container.style.background = 'white';
        container.style.padding = '6px';
        container.style.borderRadius = '8px';
        container.style.boxShadow = '0 2px 6px rgba(0,0,0,0.15)';
        container.style.minWidth = '240px';

        const input = L.DomUtil.create('input', '', container);
        input.type = 'text';
        input.placeholder = 'Search location...';
        input.style.width = '200px';
        input.style.border = '1px solid #ccd5df';
        input.style.borderRadius = '6px';
        input.style.padding = '6px 8px';
        input.style.outline = 'none';
        input.style.marginRight = '6px';

        const btn = L.DomUtil.create('button', '', container);
        btn.innerText = 'Go';
        btn.style.background = '#46955A';
        btn.style.color = 'white';
        btn.style.border = 'none';
        btn.style.borderRadius = '6px';
        btn.style.padding = '6px 10px';
        btn.style.cursor = 'pointer';

        // suggestions list
        const list = L.DomUtil.create('div', '', container);
        list.style.position = 'absolute';
        list.style.top = '42px';
        list.style.left = '6px';
        list.style.right = '6px';
        list.style.background = 'white';
        list.style.border = '1px solid #e1e5e9';
        list.style.borderRadius = '6px';
        list.style.display = 'none';
        list.style.maxHeight = '180px';
        list.style.overflow = 'auto';
        list.style.zIndex = '1000';

        let lastSearchQuery = '';
        const searchAndZoom = async (q, autoSelectFirst = false) => {
          if (!q || (q === lastSearchQuery && !autoSelectFirst)) return;
          try {
            // Add a small delay to prevent rapid API calls
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
            
            const resp = await fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(q)}&format=json&limit=5`, { 
              headers: { 'Accept-Language': 'en' },
              signal: controller.signal
            });
            clearTimeout(timeoutId);
            
            const data = await resp.json();
            if (!Array.isArray(data) || data.length === 0) {
              list.style.display = 'none';
              return;
            }
            if (autoSelectFirst) {
              const item = data[0];
              input.value = item.display_name;
              const lat = parseFloat(item.lat);
              const lng = parseFloat(item.lon);
              markerRef.current.setLatLng([lat, lng]);
              map.setView([lat, lng], 14);
              setFormData((prev) => ({ ...prev, lat: lat.toFixed(6), lng: lng.toFixed(6), locationUrl: input.value }));
              list.style.display = 'none';
              return;
            }
            // Clear previous results efficiently
            while (list.firstChild) {
              list.removeChild(list.firstChild);
            }
            // Limit to 5 results to improve performance
            const limitedData = data.slice(0, 5);
            limitedData.forEach(item => {
              const row = document.createElement('div');
              row.textContent = item.display_name;
              row.style.padding = '6px 8px';
              row.style.cursor = 'pointer';
              row.style.whiteSpace = 'nowrap';
              row.style.overflow = 'hidden';
              row.style.textOverflow = 'ellipsis';
              row.onmouseenter = () => row.style.background = '#f1f3f5';
              row.onmouseleave = () => row.style.background = 'white';
              row.onclick = () => {
                list.style.display = 'none';
                input.value = item.display_name;
                const lat = parseFloat(item.lat);
                const lng = parseFloat(item.lon);
                markerRef.current.setLatLng([lat, lng]);
                map.setView([lat, lng], 14);
                setFormData((prev) => ({ ...prev, lat: lat.toFixed(6), lng: lng.toFixed(6), locationUrl: input.value }));
              };
              list.appendChild(row);
            });
            list.style.display = 'block';
          } catch (e) {
            if (e.name !== 'AbortError') {
              console.error('Search failed', e);
            }
          }
        };

        L.DomEvent.on(btn, 'click', (e) => {
          L.DomEvent.stopPropagation(e);
          L.DomEvent.preventDefault(e);
          searchAndZoom(input.value, true);
        });

        L.DomEvent.on(input, 'keydown', (e) => {
          if (e.key === 'Enter') {
            L.DomEvent.stopPropagation(e);
            L.DomEvent.preventDefault(e);
            searchAndZoom(input.value, true);
          }
        });

        let debounceId;
        L.DomEvent.on(input, 'input', () => {
          const q = input.value.trim();
          if (debounceId) clearTimeout(debounceId);
          if (q.length < 3) { 
            list.style.display = 'none'; 
            lastSearchQuery = '';
            return; 
          }
          // Prevent duplicate searches
          if (q === lastSearchQuery) return;
          lastSearchQuery = q;
          // Increased debounce time to reduce API calls
          debounceId = setTimeout(() => {
            searchAndZoom(q, false);
          }, 600);
        });

        L.DomEvent.on(input, 'blur', () => { setTimeout(() => { list.style.display = 'none'; }, 200); });
        L.DomEvent.disableClickPropagation(container);
        return container;
      },
      onRemove: function() {}
    });

    const ctrl = new SearchControl({ position: 'topright' });
    map.addControl(ctrl);

    setTimeout(() => {
      try { map.invalidateSize(); } catch {}
    }, 100);

    window.addEventListener('resize', () => {
      try { map.invalidateSize(); } catch {}
    });
  }, [leafletReady]);

  // When lat/lng in state changes from edit or search, update marker/view
  useEffect(() => {
    if (!mapRef.current || !markerRef.current) return;
    if (!formData.lat || !formData.lng) return;
    const L = window.L;
    const lat = parseFloat(formData.lat);
    const lng = parseFloat(formData.lng);
    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      markerRef.current.setLatLng([lat, lng]);
      mapRef.current.setView([lat, lng], Math.max(mapRef.current.getZoom(), 12));
    }
  }, [formData.lat, formData.lng]);

  // Keep map card height in sync with left info card - simplified to prevent infinite loops
  useEffect(() => {
    if (!leftCardRef.current || !mapContainerRef.current) return;
    
    const syncHeights = () => {
      if (!leftCardRef.current || !mapContainerRef.current) return;
      const leftHeight = leftCardRef.current.offsetHeight;
      const headerHeight = mapHeaderRef.current ? mapHeaderRef.current.offsetHeight : 0;
      const desired = Math.max(350, Math.min(400, leftHeight - headerHeight));
      mapContainerRef.current.style.height = desired + 'px';
      
      // Use setTimeout to avoid ResizeObserver loop
      setTimeout(() => {
        try { 
          if (mapRef.current) {
            mapRef.current.invalidateSize(); 
          }
        } catch {}
      }, 100);
    };

    // Initial sync
    setTimeout(syncHeights, 300);
    
    // Only listen to window resize, not ResizeObserver
    window.addEventListener('resize', syncHeights);
    
    return () => {
      window.removeEventListener('resize', syncHeights);
    };
  }, [leafletReady]);

  const handleSearchAddress = async () => {
    if (!formData.locationUrl && !formData.name) return;
    try {
      const query = encodeURIComponent(formData.locationUrl || formData.name);
      const resp = await fetch(`https://nominatim.openstreetmap.org/search?q=${query}&format=json&limit=1` , { headers: { 'Accept-Language': 'en' } });
      const data = await resp.json();
      if (Array.isArray(data) && data.length > 0) {
        const { lat, lon } = data[0];
        setFormData((prev) => ({ ...prev, lat: Number(lat).toFixed(6), lng: Number(lon).toFixed(6) }));
      } else {
        setError('Location not found');
      }
    } catch (err) {
      console.error('Search error:', err);
      setError('Search failed');
    }
  };

  const loadRows = async () => {
    try {
      setLoading(true);
      const response = await MainService.LocationsInGrid({ Page: 1, PageSize: 100 });
      const transformed = (response?.Data || response || []).map((x, i) => ({
        id: x.Id || i + 1,
        name: x.Name || '',
        nameAr: x.NameAr || '',
        emirateName: x.Emirate?.Name || '',
        emirateId: x.EmirateId || x.Emirate?.Id || '',
        areaName: x.Area?.Name || '',
        areaId: x.AreaId || x.Area?.Id || '',
        locationUrl: x.LocationUrl || '',
        lat: x.LAT || '',
        lng: x.LNG || '',
      }));
      setLocations(transformed);
      setFilteredRows(transformed);
    } catch (err) {
      console.error('Load locations error:', err);
      setError('Failed to load locations');
      setLocations([]);
      setFilteredRows([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setFormData({ name: '', nameAr: '', emirateId: '', areaId: '', locationUrl: '', lat: '', lng: '' });
    setEditing(null);
  };

  const handleRefresh = async () => {
    resetForm();
    setSearchTerm('');
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      await loadRows();
    } catch (err) {
      console.error('Error refreshing locations:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.areaId) { setError('Select Area'); return; }
    if (!formData.emirateId) { setError('Select Emirate'); return; }
    if (!formData.name.trim()) { setError('Enter Name'); return; }
    try {
      setLoading(true);
      const payload = {
        Name: formData.name.trim(),
        NameAr: formData.nameAr.trim(),
        EmirateId: formData.emirateId,
        AreaId: formData.areaId,
        LocationUrl: formData.locationUrl,
        LAT: `${formData.lat || ''}`,
        LNG: `${formData.lng || ''}`,
      };
      if (editing?.id) payload.Id = editing.id;
      const resp = await MainService.SaveLocation(payload);
      if (resp === true || resp === 'true' || resp === 'Success') {
        setSuccess(editing ? 'Location updated successfully' : 'Location created successfully');
        await loadRows();
        resetForm();
      } else if (resp === 'Failed') {
        setError('Error');
      } else {
        setError(resp || 'Failed to save location');
      }
    } catch (err) {
      console.error('Save location error:', err);
      setError('Failed to save location');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (row) => {
    setEditing(row);
    setFormData({
      name: row.name || '',
      nameAr: row.nameAr || '',
      emirateId: row.emirateId || '',
      areaId: row.areaId || '',
      locationUrl: row.locationUrl || '',
      lat: row.lat || '',
      lng: row.lng || ''
    });
    setError('');
    setSuccess('');
    setTimeout(() => {
      const el = document.querySelector('.form-section');
      if (el) window.scrollTo({ top: el.offsetTop - 100, behavior: 'smooth' });
    }, 50);
  };

  const handleDelete = (row) => {
    setRowToDelete(row);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!rowToDelete) return;
    try {
      setLoading(true);
      setDeleteDialogOpen(false);
      const resp = await MainService.DeleteLocation(rowToDelete.id);
      if (resp === 'Success' || resp === true || resp === 'true') {
        setSuccess('Location deleted successfully');
        await loadRows();
      } else {
        if (typeof resp === 'string' && resp.trim().length > 0) {
          setWarning(resp);
        } else {
          setError(resp || 'Failed to delete location');
        }
      }
    } catch (err) {
      console.error('Delete location error:', err);
      setError('Failed to delete location');
    } finally {
      setLoading(false);
      setRowToDelete(null);
    }
  };

  const cancelDelete = () => {
    setDeleteDialogOpen(false);
    setRowToDelete(null);
  };

  const handleSearch = (event) => {
    const value = event.target.value.toLowerCase();
    setSearchTerm(value);
    const filtered = locations.filter((x) =>
      (x.name || '').toLowerCase().includes(value) ||
      (x.nameAr || '').toLowerCase().includes(value) ||
      (x.areaName || '').toLowerCase().includes(value) ||
      (x.emirateName || '').toLowerCase().includes(value)
    );
    setFilteredRows(filtered);
  };

  const handleSort = (field) => {
    const direction = sortField === field && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortDirection(direction);
    const sorted = [...filteredRows].sort((a, b) => {
      const aVal = a[field];
      const bVal = b[field];
      if (direction === 'asc') return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
    });
    setFilteredRows(sorted);
  };

  // Upload functionality
  const handleUploadClick = () => {
    setUploadDialogOpen(true);
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const validTypes = ['application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'text/csv'];
      if (!validTypes.includes(file.type) && !file.name.endsWith('.csv') && !file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
        setError('Please select a valid Excel or CSV file');
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleUploadSubmit = async () => {
    if (!selectedFile) {
      setError('Please select a file to upload');
      return;
    }
    try {
      setLoading(true);
      const formDataUpload = new FormData();
      formDataUpload.append('file', selectedFile);
      const resp = await MainService.UploadLocations(formDataUpload);
      if (resp === true || resp === 'true' || resp === 'Success') {
        setSuccess('Locations uploaded successfully');
        await loadRows();
        setUploadDialogOpen(false);
        setSelectedFile(null);
      } else {
        if (typeof resp === 'string' && resp.trim().length > 0) {
          setWarning(resp);
        } else {
          setError('Failed to upload locations');
        }
      }
    } catch (err) {
      console.error('Upload error:', err);
      setError('Failed to upload locations');
    } finally {
      setLoading(false);
    }
  };

  const cancelUpload = () => {
    setUploadDialogOpen(false);
    setSelectedFile(null);
  };

  // Bulk Edit functionality
  const handleBulkEditClick = async () => {
    try {
      setBulkEditLoading(true);
      setTimeout(() => {
        const limitedLocations = locations.slice(0, 50);
        setBulkEditData(limitedLocations.map(loc => ({ ...loc, isModified: false })));
        setBulkEditDialogOpen(true);
        setBulkEditLoading(false);
      }, 100);
    } catch (err) {
      console.error('Bulk edit preparation error:', err);
      setError('Failed to prepare bulk edit');
      setBulkEditLoading(false);
    }
  };

  const handleBulkEditChange = (index, field, value) => {
    const updated = [...bulkEditData];
    updated[index] = { ...updated[index], [field]: value, isModified: true };
    setBulkEditData(updated);
  };

  const handleBulkEditSubmit = async () => {
    const modifiedRows = bulkEditData.filter(row => row.isModified);
    if (modifiedRows.length === 0) {
      setWarning('No changes detected');
      return;
    }
    try {
      setLoading(true);
      const payload = modifiedRows.map(row => ({
        Id: row.id,
        Name: row.name,
        NameAr: row.nameAr,
        EmirateId: row.emirateId,
        AreaId: row.areaId,
        LocationUrl: row.locationUrl,
        LAT: row.lat,
        LNG: row.lng
      }));
      const resp = await MainService.BulkUpdateLocations(payload);
      if (resp === true || resp === 'true' || resp === 'Success') {
        setSuccess(`${modifiedRows.length} location(s) updated successfully`);
        await loadRows();
        setBulkEditDialogOpen(false);
        setBulkEditData([]);
      } else {
        if (typeof resp === 'string' && resp.trim().length > 0) {
          setWarning(resp);
        } else {
          setError('Failed to update locations');
        }
      }
    } catch (err) {
      console.error('Bulk update error:', err);
      setError('Failed to update locations');
    } finally {
      setLoading(false);
    }
  };

  const cancelBulkEdit = () => {
    setBulkEditDialogOpen(false);
    setBulkEditData([]);
  };

  const handleExportExcel = () => {
    try {
      const headers = ['Serial No', 'Name', 'Name Ar', 'Emirate', 'Area', 'Latitude', 'Longitude', 'Location URL'];
      const csvContent = [
        headers.join(','),
        ...filteredRows.map((row, index) => [
          index + 1,
          `"${row.name}"`,
          `"${row.nameAr}"`,
          `"${row.emirateName}"`,
          `"${row.areaName}"`,
          `"${row.lat}"`,
          `"${row.lng}"`,
          `"${row.locationUrl}"`
        ].join(','))
      ].join('\n');

      const BOM = '\uFEFF';
      const csvWithBOM = BOM + csvContent;
      const blob = new Blob([csvWithBOM], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `locations_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      setSuccess('Locations data exported successfully');
    } catch (err) {
      console.error('Error exporting data:', err);
      setError('Failed to export data');
    }
  };

  return (
    <div className="space-y-6">
      {/* Toast Messages */}
      {error && (
        <div className="fixed top-4 right-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 min-w-[300px]">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button onClick={() => setError('')} className="text-red-500 hover:text-red-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="fixed top-4 right-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 min-w-[300px]">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{success}</span>
          <button onClick={() => setSuccess('')} className="text-green-500 hover:text-green-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {warning && (
        <div className="fixed top-20 right-4 bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 min-w-[300px]">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{warning}</span>
          <button onClick={() => setWarning('')} className="text-yellow-500 hover:text-yellow-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Form and Map Section */}
      <div className="grid grid-cols-12 gap-4">
        {/* Left Form Card */}
        <div className="col-span-7 bg-white rounded-lg shadow-sm border border-gray-200 p-6" ref={leftCardRef}>
          <div className="mb-6 pb-4 border-b border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center">
                <MapPin className="w-6 h-6 text-primary mr-2" />
                <h1 className="text-3xl font-bold text-gray-900">Locations</h1>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleUploadClick}
                  className="p-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-300 transition-colors"
                  title="Upload"
                >
                  <Upload className="w-5 h-5" />
                </button>
                <button
                  onClick={handleBulkEditClick}
                  disabled={bulkEditLoading}
                  className="p-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-300 transition-colors disabled:opacity-50"
                  title="Bulk Edit"
                >
                  {bulkEditLoading ? (
                    <RefreshCw className="w-5 h-5 animate-spin" />
                  ) : (
                    <FileEdit className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>
            <p className="text-sm text-gray-500 ml-8">Manage locations with emirate and area mapping</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-12 gap-4 items-end">
              <div className="col-span-6">
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  Name (English) <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-primary bg-transparent"
                  placeholder=""
                />
              </div>

              <div className="col-span-6">
                <label htmlFor="nameAr" className="block text-sm font-medium text-gray-700 mb-2">
                  Name (Arabic)
                </label>
                <input
                  type="text"
                  id="nameAr"
                  name="nameAr"
                  value={formData.nameAr}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-primary bg-transparent"
                  placeholder=""
                  dir="rtl"
                />
              </div>
            </div>

            <div className="grid grid-cols-12 gap-4 items-end">
              <div className="col-span-6">
                <label htmlFor="emirateId" className="block text-sm font-medium text-gray-700 mb-2">
                  Emirate <span className="text-red-500">*</span>
                </label>
                <select
                  id="emirateId"
                  name="emirateId"
                  value={formData.emirateId}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-primary bg-transparent"
                >
                  <option value="">Select Emirate</option>
                  {emirates.map((e) => (
                    <option key={e.Id} value={e.Id}>{e.Name}</option>
                  ))}
                </select>
              </div>

              <div className="col-span-6">
                <label htmlFor="areaId" className="block text-sm font-medium text-gray-700 mb-2">
                  Area <span className="text-red-500">*</span>
                </label>
                <select
                  id="areaId"
                  name="areaId"
                  value={formData.areaId}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-primary bg-transparent"
                >
                  <option value="">Select Area</option>
                  {areas.map((a) => (
                    <option key={a.Id} value={a.Id}>{a.Name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-12 gap-4 items-end">
              <div className="col-span-9">
                <label htmlFor="locationUrl" className="block text-sm font-medium text-gray-700 mb-2">
                  Search address or paste location URL
                </label>
                <input
                  type="text"
                  id="locationUrl"
                  name="locationUrl"
                  value={formData.locationUrl}
                  onChange={handleInputChange}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSearchAddress(); } }}
                  className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-primary bg-transparent"
                  placeholder=""
                />
              </div>
              <div className="col-span-3">
                <button
                  type="button"
                  onClick={handleSearchAddress}
                  className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-300 transition-colors flex items-center justify-center gap-2"
                >
                  <Search className="w-4 h-4" />
                  Search
                </button>
              </div>
            </div>

            <div className="grid grid-cols-12 gap-4 items-end">
              <div className="col-span-4">
                <label htmlFor="lat" className="block text-sm font-medium text-gray-700 mb-2">
                  Latitude
                </label>
                <input
                  type="text"
                  id="lat"
                  name="lat"
                  value={formData.lat}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-primary bg-transparent"
                  placeholder=""
                />
              </div>

              <div className="col-span-4">
                <label htmlFor="lng" className="block text-sm font-medium text-gray-700 mb-2">
                  Longitude
                </label>
                <input
                  type="text"
                  id="lng"
                  name="lng"
                  value={formData.lng}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-primary bg-transparent"
                  placeholder=""
                />
              </div>

              <div className="col-span-4 flex items-center gap-2">
                <button
                  type="submit"
                  disabled={loading}
                  className="p-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  title={editing ? 'Update' : 'Save'}
                >
                  {loading ? (
                    <RefreshCw className="w-5 h-5 animate-spin" />
                  ) : (
                    <Save className="w-5 h-5" />
                  )}
                </button>
                <button
                  type="button"
                  onClick={handleRefresh}
                  className="p-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-300 transition-colors"
                  title="Refresh"
                >
                  <RefreshCw className="w-5 h-5" />
                </button>
              </div>
            </div>
          </form>
        </div>

        {/* Right Map Card */}
        <div className="col-span-5 bg-white rounded-lg shadow-sm border border-gray-200 flex flex-col" ref={mapCardRef}>
          <div className="p-4 border-b border-gray-200" ref={mapHeaderRef}>
            <div className="flex items-center">
              <Navigation className="w-5 h-5 text-primary mr-2" />
              <h3 className="text-lg font-semibold text-gray-900">Pick Location on Map</h3>
            </div>
          </div>
          <div className="p-4 overflow-hidden">
            <div ref={mapContainerRef} id="leaflet-map" className="w-full rounded-lg overflow-hidden bg-gray-100" style={{ height: '350px', maxHeight: '350px' }} />
            {!leafletReady && (
              <div className="p-3 text-gray-500 text-center">Loading map...</div>
            )}
          </div>
        </div>
      </div>

      {/* Table Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 flex flex-col">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center">
            <List className="w-5 h-5 text-primary mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">Locations List</h3>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="absolute left-0 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search locations..."
                value={searchTerm}
                onChange={handleSearch}
                className="pl-8 pr-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-primary bg-transparent w-64"
              />
            </div>
            <button
              onClick={handleExportExcel}
              className="p-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-300 transition-colors"
              title="Export Excel"
            >
              <Download className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-x-auto max-h-96 overflow-y-auto relative">
          {loading && (
            <div className="absolute inset-0 bg-white bg-opacity-75 backdrop-blur-sm z-20 flex items-center justify-center">
              <div className="flex flex-col items-center gap-2">
                <RefreshCw className="w-8 h-8 text-primary animate-spin" />
                <span className="text-sm text-gray-600">Loading...</span>
              </div>
            </div>
          )}
          <table className="w-full table-fixed">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                <th onClick={() => handleSort('id')} className="w-[5%] px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100">
                  <div className="flex items-center">
                    # {sortField === 'id' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </div>
                </th>
                <th onClick={() => handleSort('name')} className="w-[15%] px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100">
                  <div className="flex items-center">
                    Name {sortField === 'name' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </div>
                </th>
                <th onClick={() => handleSort('nameAr')} className="w-[15%] px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100">
                  <div className="flex items-center">
                    Name Ar {sortField === 'nameAr' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </div>
                </th>
                <th onClick={() => handleSort('emirateName')} className="w-[12%] px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100">
                  <div className="flex items-center">
                    Emirate {sortField === 'emirateName' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </div>
                </th>
                <th onClick={() => handleSort('areaName')} className="w-[12%] px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100">
                  <div className="flex items-center">
                    Area {sortField === 'areaName' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </div>
                </th>
                <th className="w-[12%] px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Coordinates</th>
                <th className="w-[12%] px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location Url</th>
                <th className="w-[10%] px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredRows.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-4 py-6 text-center text-gray-500">
                    No locations found
                  </td>
                </tr>
              ) : (
                filteredRows.map((row, index) => (
                  <tr key={row.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{index + 1}</td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{row.name}</td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 text-left">{row.nameAr}</td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{row.emirateName}</td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{row.areaName}</td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{row.lat},{row.lng}</td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                      {row.locationUrl && (
                        <a href={row.locationUrl} target="_blank" rel="noreferrer" className="text-primary hover:underline">Open</a>
                      )}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-center">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => handleEdit(row)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(row)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="bg-gray-50 border-t border-gray-200 px-4 py-3">
          <div className="flex items-center justify-between text-sm text-gray-600">
            <span>Total Locations: {filteredRows.length}</span>
            <span>Showing {filteredRows.length} of {locations.length} locations</span>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      {deleteDialogOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="bg-red-50 border-b border-red-200 px-6 py-4 rounded-t-lg">
              <div className="flex items-center">
                <AlertTriangle className="w-5 h-5 text-red-600 mr-2" />
                <h3 className="text-lg font-semibold text-red-900">Confirm Delete</h3>
              </div>
            </div>
            <div className="px-6 py-4">
              <p className="text-gray-700 mb-2">
                Are you sure you want to delete the location <strong>"{rowToDelete?.name}"</strong>?
              </p>
              <p className="text-sm text-gray-500">
                This action cannot be undone and will permanently remove the location from the system.
              </p>
            </div>
            <div className="px-6 py-4 bg-gray-50 rounded-b-lg flex justify-end gap-3">
              <button
                onClick={cancelDelete}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={loading}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
              >
                {loading ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Dialog */}
      {uploadDialogOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4">
            <div className="bg-blue-50 border-b border-blue-200 px-6 py-4 rounded-t-lg">
              <div className="flex items-center">
                <Upload className="w-5 h-5 text-blue-600 mr-2" />
                <h3 className="text-lg font-semibold text-blue-900">Upload Locations</h3>
              </div>
            </div>
            <div className="px-6 py-4">
              <p className="text-sm text-gray-600 mb-4">
                Upload an Excel or CSV file containing location data. The file should include columns: Name, Name Ar, Emirate, Area, Location URL, Latitude, Longitude.
              </p>
              <input
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleFileChange}
                style={{ display: 'none' }}
                id="upload-file-input"
              />
              <label htmlFor="upload-file-input">
                <div className="w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-primary transition-colors text-center">
                  {selectedFile ? (
                    <div className="flex items-center justify-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <span className="text-sm text-gray-700">{selectedFile.name}</span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center gap-2">
                      <Upload className="w-5 h-5 text-gray-400" />
                      <span className="text-sm text-gray-600">Choose File</span>
                    </div>
                  )}
                </div>
              </label>
            </div>
            <div className="px-6 py-4 bg-gray-50 rounded-b-lg flex justify-end gap-3">
              <button
                onClick={cancelUpload}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleUploadSubmit}
                disabled={loading || !selectedFile}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
              >
                {loading ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Upload
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Edit Dialog */}
      {bulkEditDialogOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full mx-4 max-h-[90vh] flex flex-col">
            <div className="bg-yellow-50 border-b border-yellow-200 px-6 py-4 rounded-t-lg">
              <div className="flex items-center">
                <FileEdit className="w-5 h-5 text-yellow-600 mr-2" />
                <h3 className="text-lg font-semibold text-yellow-900">Bulk Edit Locations</h3>
              </div>
            </div>
            <div className="px-6 py-4 overflow-auto flex-1">
              <p className="text-sm text-gray-600 mb-4">
                Edit multiple locations at once. Modified rows will be highlighted and saved when you click Update.
                {bulkEditData.length >= 50 && (
                  <span className="text-yellow-600 font-bold"> Showing first 50 locations only.</span>
                )}
              </p>
              {bulkEditLoading ? (
                <div className="text-center py-8">
                  <RefreshCw className="w-8 h-8 text-primary animate-spin mx-auto mb-2" />
                  <span className="text-gray-600">Preparing bulk edit...</span>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Name Ar</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Emirate</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Area</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Latitude</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Longitude</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {bulkEditData.map((row, index) => (
                        <tr key={row.id} className={row.isModified ? 'bg-yellow-50' : ''}>
                          <td className="px-3 py-2">{index + 1}</td>
                          <td className="px-3 py-2">
                            <input
                              type="text"
                              value={row.name}
                              onChange={(e) => handleBulkEditChange(index, 'name', e.target.value)}
                              className="w-full px-2 py-1 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-primary bg-transparent text-sm"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <input
                              type="text"
                              value={row.nameAr}
                              onChange={(e) => handleBulkEditChange(index, 'nameAr', e.target.value)}
                              className="w-full px-2 py-1 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-primary bg-transparent text-sm"
                              dir="rtl"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <select
                              value={row.emirateId}
                              onChange={(e) => handleBulkEditChange(index, 'emirateId', e.target.value)}
                              className="w-full px-2 py-1 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-primary bg-transparent text-sm"
                            >
                              {emirates.map((e) => (
                                <option key={e.Id} value={e.Id}>{e.Name}</option>
                              ))}
                            </select>
                          </td>
                          <td className="px-3 py-2">
                            <select
                              value={row.areaId}
                              onChange={(e) => handleBulkEditChange(index, 'areaId', e.target.value)}
                              className="w-full px-2 py-1 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-primary bg-transparent text-sm"
                            >
                              {areas.map((a) => (
                                <option key={a.Id} value={a.Id}>{a.Name}</option>
                              ))}
                            </select>
                          </td>
                          <td className="px-3 py-2">
                            <input
                              type="text"
                              value={row.lat}
                              onChange={(e) => handleBulkEditChange(index, 'lat', e.target.value)}
                              className="w-full px-2 py-1 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-primary bg-transparent text-sm"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <input
                              type="text"
                              value={row.lng}
                              onChange={(e) => handleBulkEditChange(index, 'lng', e.target.value)}
                              className="w-full px-2 py-1 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-primary bg-transparent text-sm"
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="px-6 py-4 bg-gray-50 rounded-b-lg flex justify-end gap-3">
              <button
                onClick={cancelBulkEdit}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkEditSubmit}
                disabled={loading}
                className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
              >
                {loading ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Updating...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Update All
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Locations;
